import { useState } from 'react';
import { Box, Button, Chip, IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { STARTERS } from './AssistantChat';

const CLAUDE_NEW_CHAT_URL = 'https://claude.ai/new';

/**
 * For someone who asks on their Claude subscription: the question is typed
 * here and opens as a new chat in Claude, where the Diwan connector answers
 * it. The conversation itself lives in Claude, not in Diwan.
 */
export function AssistantHandoff({ onSetup }: { onSetup: () => void }) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');

  const askInClaude = (text: string) => {
    const q = text.trim();
    // Naming Diwan steers Claude to the connector rather than answering from
    // what it already knows.
    const url = q ? `${CLAUDE_NEW_CHAT_URL}?q=${encodeURIComponent(`${t('From Diwan:')} ${q}`)}` : CLAUDE_NEW_CHAT_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
    setQuestion('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack spacing={1.25} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('Your questions open in Claude and use your Claude subscription. Diwan only lets Claude read — never change — what you are allowed to see.')}
        </Typography>
        {STARTERS.map((starter) => (
          <Chip
            key={starter}
            label={t(starter)}
            onClick={() => askInClaude(t(starter))}
            variant="outlined"
            icon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
          />
        ))}
        <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => askInClaude('')} sx={{ alignSelf: 'flex-start' }}>
          {t('Open Claude')}
        </Button>
        <Link component="button" variant="caption" onClick={onSetup} sx={{ alignSelf: 'flex-start' }}>
          {t('Show setup steps')}
        </Link>
      </Stack>

      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (question.trim()) askInClaude(question);
        }}
        sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: 1, borderColor: 'divider' }}
      >
        <TextField
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={t('Ask in Claude…')}
          size="small"
          fullWidth
          multiline
          maxRows={4}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (question.trim()) askInClaude(question);
            }
          }}
        />
        <IconButton type="submit" color="primary" disabled={!question.trim()} aria-label={t('Ask in Claude…')}>
          <SendIcon sx={{ transform: (th) => (th.direction === 'rtl' ? 'scaleX(-1)' : undefined) }} />
        </IconButton>
      </Box>
    </Box>
  );
}
